/**
 * The panel that answers "who is this organisation".
 *
 * It is the one question on this site that no filing asks. Everything else
 * here is a disclosure read back — a cheque, a roll call, an issue code —
 * and can be checked against the document it came off. "Who AIPAC is"
 * cannot: there is no form with that on it.
 *
 * So the panel is built out of forms that were answering something else,
 * in layers, and the layers are kept visibly apart:
 *
 * 1. **Their own words.** The one-line description each registrant writes
 *    on its LD-1. Sworn, published, and quoted as theirs — which is why it
 *    is set as a quotation with the register's own entry linked beside it,
 *    and never rendered as though the site were speaking.
 *
 *    A third of the organisations that give money leave that line blank,
 *    and not the small third: the Realtors, the teachers' union, Amazon,
 *    Comcast, Lockheed and Chevron are all in it. For them the panel falls
 *    back to the free text on a quarterly LD-2, which is the same kind of
 *    thing off a different form — sworn, published, and about three months
 *    of work rather than about the organisation. That difference is on the
 *    page: a separate attribution, a link to that filing rather than to the
 *    register, the register's own gap stated underneath, and an ellipsis
 *    wherever the filer wrote more than is shown.
 * 2. **What they lobby about, and who pays them to.** Issue codes off the
 *    quarterly LD-2s, and the clients whose interests those filings name.
 * 3. **The people.** The lobbyists their filings name, the government post
 *    each swore to holding before, and the members that post names.
 *
 * **Nothing here is generated and nothing here is written.** The two
 * sentences the panel composes are arithmetic over the payload — a count
 * and a share — and they live in `profile.ts` where they can be tested.
 *
 * That is a deliberate refusal rather than a gap. This repo does generate
 * prose for laws, and the reason it can is that a statute determines what a
 * summary of it may say: the model is paraphrasing a document, the document
 * is on the page, and a reader can hold one against the other. An
 * organisation profile has no such document. A paragraph about AIPAC would
 * be interpolation between the facts below rather than a reading of any one
 * of them, and its failure mode is a fluent sentence naming a president who
 * left three years ago — unverifiable by construction, and precisely the
 * thing this site exists not to do.
 *
 * Self-contained on purpose: it fetches its own payload from its own
 * endpoint, so it can be dropped into a page without that page knowing
 * anything about it beyond the id it is about.
 */

import { Link } from "react-router-dom";
import { api, type Profile, type ProfileFunder, type ProfilePerson } from "../api";
import { useAsync } from "../useAsync";
import { lobbyistPath } from "./lobbyist";
import { brief } from "./party";
import {
  bench,
  benchSentence,
  concentration,
  insteadOfTheRegister,
  whoPays,
} from "./profile";

/** One lobbying registrant. */
export function OrganisationProfile({ registrantId }: { registrantId: number }) {
  const found = useAsync(() => api.firmProfile(registrantId), [registrantId]);
  return <Panel profile={found.data} />;
}

/**
 * One party committee, keyed on its FEC id.
 *
 * `omitFunders` exists for the committee detail page, which lists the same
 * LD-203 money at full length under its own heading and publishes how each
 * filer's spelling was matched underneath it. The panel's short version of
 * that list two screens above would read as a second, smaller figure for
 * the same thing.
 */
export function CommitteeProfile({
  committeeId,
  omitFunders = false,
}: {
  committeeId: string;
  omitFunders?: boolean;
}) {
  const found = useAsync(() => api.committeeProfile(committeeId), [committeeId]);
  return <Panel profile={found.data} omitFunders={omitFunders} />;
}

/**
 * Nothing at all while it loads or if it fails.
 *
 * A spinner belongs to a page the reader is waiting for. This is a panel
 * beside the record they came for, and every figure on the rest of the page
 * is already there — so a failure here should cost the panel and not put an
 * error box between the reader and the money.
 */
function Panel({
  profile,
  omitFunders,
}: {
  profile: Profile | null;
  omitFunders?: boolean;
}) {
  if (!profile) return null;
  return <WhoTheyAre profile={profile} omitFunders={omitFunders} />;
}

export function WhoTheyAre({
  profile,
  omitFunders = false,
}: {
  profile: Profile;
  /** Set by a page that already shows this organisation's funders itself. */
  omitFunders?: boolean;
}) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-semibold">Who they are</h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        No federal form asks an organisation to describe itself for a page like this one, so this
        is assembled from forms that were asked something else — and nothing on it is written or
        generated.
      </p>

      {profile.empty ? (
        <Nothing profile={profile} />
      ) : (
        <div className="mt-5 grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <div className="space-y-8">
            <InTheirOwnWords profile={profile} />
            <WhatTheyLobbyAbout profile={profile} />
          </div>
          <div className="space-y-8">
            {!omitFunders && <WhoPaysThem profile={profile} />}
            <ThePeople profile={profile} />
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The common case, said once and quietly.
 *
 * Most of the register is this: one filing, a name, and nothing else. Four
 * empty headings would read as a broken page, and padding them would be
 * worse — so the panel says what it has and stops.
 */
function Nothing({ profile }: { profile: Profile }) {
  return (
    <p className="mt-4 max-w-3xl rounded-2xl border border-line bg-sunk p-5 text-muted">
      Nothing on file. {profile.name} appears in this corpus through its money alone — no
      self-description on the register, no disclosed lobbying subjects, and no individual named on
      a filing. That is the ordinary state of most of the register rather than a gap in what is
      held here, and the figures elsewhere on this page are unaffected by it.
    </p>
  );
}

/**
 * Layer one: the organisation describing itself on a federal form.
 *
 * Set as a quotation with the attribution and the register's own entry
 * attached, and those never come apart from the text. A description
 * rendered as body copy is a description the site appears to be asserting,
 * and the whole reason this one is trustworthy is that it is not ours.
 */
function InTheirOwnWords({ profile }: { profile: Profile }) {
  const said = profile.said;
  if (said) {
    // A quote off a quarterly report rather than the register carries the
    // register's own gap with it. Without that line a reader has no way to
    // tell why this organisation is quoted from a different form than the
    // one they read a page earlier.
    const instead = said.link !== "Their entry on the register" && profile.refused;
    return (
      <div>
        <h3 className="font-display text-xl font-semibold">In their own words</h3>
        <blockquote className="mt-3 border-l-4 border-brand/50 bg-brand-soft/40 py-4 pr-5 pl-5">
          <p className="font-display text-lg leading-relaxed">
            {/* The ellipsis sits inside the quotation marks because that is
                what it is: the filer's paragraph, stopped early. Outside
                them it would read as the page trailing off. */}
            “{said.text}
            {said.trimmed && "…"}”
          </p>
          <footer className="mt-3 text-sm text-muted">
            {said.attribution}
            {said.since && said.year && said.since < said.year
              ? `, unchanged since ${said.since}`
              : ""}
            .{" "}
            <a
              href={said.source}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand hover:underline"
            >
              {said.trimmed ? `${said.link}, in full` : said.link}
            </a>
          </footer>
        </blockquote>
        {instead && (
          <p className="mt-3 max-w-prose text-sm text-muted">
            {insteadOfTheRegister(profile)}
          </p>
        )}
        <Facts profile={profile} />
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-display text-xl font-semibold">In their own words</h3>
      <p className="mt-3 max-w-prose text-muted">{noDescription(profile)}</p>
      <Facts profile={profile} />
    </div>
  );
}

/**
 * Why there is no quotation, in the terms the register left it in.
 *
 * "Nothing on file" and "the description field holds a registration date"
 * are different facts and only one of them is about the filer. Saying which
 * is also the only way a reader can tell our rule from the register's gap —
 * this rule will be wrong about somebody, and it should be visible when it
 * is.
 */
function noDescription(profile: Profile): string {
  if (profile.kind === "committee") {
    return `There is no self-description to quote: ${profile.refused}. The FEC registers a committee's officers and its money, not an account of what it is for.`;
  }
  if (!profile.refused) {
    return "The register's own entry for them has not been read yet, so there is no self-description here to quote. It arrives with the next sweep of the register rather than with the next filing.";
  }
  if (profile.refused === "blank") {
    return "Their entry on the register leaves the description field blank, which is true of most of it. Nothing has been substituted for it.";
  }
  return `Their entry on the register holds ${profile.refused} in the description field rather than a description of what they do, so there is nothing here worth quoting.`;
}

/** The registration facts, which are short enough to read as one line. */
function Facts({ profile }: { profile: Profile }) {
  if (profile.facts.length === 0) return null;
  return (
    <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
      {profile.facts.map((fact) => (
        <div key={fact.label}>
          <dt className="text-xs font-semibold tracking-wide text-faint uppercase">{fact.label}</dt>
          <dd className="mt-0.5 font-medium">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Layer one, continued: the issue codes.
 *
 * Coarse and universal. Every disclosed activity carries one where a named
 * bill appears on about one in eight, so this is the only subject most of
 * the record has — and a distribution over it is a factual profile of what
 * an organisation spends its lobbying on.
 */
function WhatTheyLobbyAbout({ profile }: { profile: Profile }) {
  if (profile.subjects.length === 0) return null;
  const said = concentration(profile);
  const widest = profile.subjects[0].share || 1;
  return (
    <div>
      <h3 className="font-display text-xl font-semibold">What they lobby about</h3>
      {said && <p className="mt-1.5 max-w-prose text-muted">{said}</p>}
      <ol className="mt-4 space-y-2">
        {profile.subjects.slice(0, 8).map((subject) => (
          <li key={subject.code} className="flex items-center gap-3 text-sm">
            <span className="tnum w-10 rounded bg-sunk px-1.5 py-0.5 text-center text-xs font-semibold text-muted">
              {subject.code}
            </span>
            <span className="min-w-32 flex-1 truncate">{subject.label || subject.code}</span>
            {/* Widest bar to the largest subject rather than to 100%. The
                interesting comparison is between the codes, and against a
                full-width scale a firm spread over twenty of them draws
                twenty slivers. */}
            <span className="h-2 w-24 overflow-hidden rounded-full bg-sunk">
              <span
                className="block h-full rounded-full bg-brand/70"
                style={{ width: `${(subject.share / widest) * 100}%` }}
              />
            </span>
            <span className="tnum w-9 text-right text-muted">
              {Math.round(subject.share * 100)}%
            </span>
          </li>
        ))}
      </ol>
      {profile.subjects.length > 8 && (
        <p className="mt-2 text-sm text-faint">
          and {(profile.subjects.length - 8).toLocaleString()} more subjects, each below{" "}
          {Math.round(profile.subjects[7].share * 100)}%.
        </p>
      )}
    </div>
  );
}

/**
 * Layer one, continued: who is paying for the lobbying.
 *
 * A firm's clients are the substance of what it is — K&L Gates is not an
 * opinion about energy policy, it is a hundred and twenty-nine clients who
 * each hold one. A committee's are its donors, which is money rather than a
 * retainer, so the two are labelled and ordered differently and the
 * sentence above them declines to run for a committee at all.
 */
function WhoPaysThem({ profile }: { profile: Profile }) {
  if (profile.funders.length === 0) return null;
  const committee = profile.kind === "committee";
  const said = whoPays(profile);
  // An organisation lobbying for itself has one row and the sentence above
  // has already said what it is. Listing "AIPAC · 7 activities" under "who
  // pays them" is a heading spent on a fact the reader has just read.
  const list = profile.self_funded ? [] : profile.funders.slice(0, 8);
  return (
    <div>
      <h3 className="font-display text-xl font-semibold">
        {committee ? "Who funds them" : "Who pays them to"}
      </h3>
      {said && <p className="mt-1.5 max-w-prose text-muted">{said}</p>}
      {committee && (
        <p className="mt-1.5 max-w-prose text-sm text-muted">
          The organisations whose lobbyists certified giving to its federal account, largest first.
          Their own filings, not the committee's.
        </p>
      )}
      {list.length > 0 && (
        <ol className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
          {list.map((funder) => (
            <li key={`${funder.id ?? funder.name}`}>
              <Funder funder={funder} committee={committee} />
            </li>
          ))}
        </ol>
      )}
      {profile.funders_total > list.length && list.length > 0 && (
        <p className="mt-2 text-sm text-faint">
          {(profile.funders_total - list.length).toLocaleString()} more, not shown.
        </p>
      )}
    </div>
  );
}

function Funder({ funder, committee }: { funder: ProfileFunder; committee: boolean }) {
  const body = (
    <>
      <span className="min-w-40 flex-1 font-medium">{funder.name}</span>
      {committee ? (
        <span className="tnum text-sm font-medium">{brief(funder.amount ?? 0)}</span>
      ) : (
        <span className="text-sm text-faint">
          {(funder.activities ?? 0).toLocaleString()}{" "}
          {funder.activities === 1 ? "activity" : "activities"}
        </span>
      )}
    </>
  );
  // Only a committee's funders have a page of their own here: they are
  // registrants. A client is named on a filing and has no page, and linking
  // one to the registrant index by name would be a guess.
  return committee && funder.id !== null ? (
    <Link
      to={`/money/${funder.id}`}
      className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-2.5 transition hover:bg-sunk"
    >
      {body}
    </Link>
  ) : (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-2.5">{body}</div>
  );
}

/**
 * Layer two: the named people, which is the half nobody else presents.
 *
 * Two filings that never reference each other. A covered position is sworn
 * on a quarterly LD-2 — the government job somebody held before they were a
 * lobbyist, in their own words, usually naming the member they worked for.
 * The money is on a semi-annual LD-203, and only where the individual filed
 * it rather than their employer. Putting them beside each other is done
 * here and, as far as we can tell, nowhere else.
 */
function ThePeople({ profile }: { profile: Profile }) {
  if (profile.people.length === 0) return null;
  const { revolved, tied } = bench(profile);
  return (
    <div>
      <h3 className="font-display text-xl font-semibold">
        {profile.kind === "committee" ? "Its officers" : "The people"}
      </h3>
      {profile.kind === "committee" ? (
        <p className="mt-1.5 max-w-prose text-muted">
          The treasurer, off the FEC's committee master. Who sits on a party committee is disclosed
          nowhere, so this is the only officer of it anybody publishes.
        </p>
      ) : (
        <p className="mt-1.5 max-w-prose text-muted">
          {revolved > 0 ? (
            <>
              <span className="font-medium text-ink">
                {benchSentence(revolved, profile.people_total)}
              </span>
              {tied > 0 && (
                <>
                  , for {tied === 1 ? "a member" : "members"} of Congress named below
                </>
              )}
              . Quoted from the form, in their own words.
            </>
          ) : (
            <>
              Named because a contribution report was filed in their name. Somebody who neither
              swore to a prior government post nor had a report filed in their name is not in this
              record at all, so this is a floor on the bench rather than the whole of it.
            </>
          )}
        </p>
      )}
      <ol className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
        {profile.people.map((person) => (
          <li key={person.name} className="px-4 py-3">
            {/* Only a registrant's people have a page. The key is the
                organisation and the name together, and a committee's
                treasurer is not named on anybody's lobbying register. */}
            <Person
              person={person}
              registrantId={profile.kind === "registrant" ? Number(profile.id) : null}
            />
          </li>
        ))}
      </ol>
      {profile.people_total > profile.people.length && (
        <p className="mt-2 text-sm text-faint">
          and {(profile.people_total - profile.people.length).toLocaleString()} more named on these
          filings.
        </p>
      )}
    </div>
  );
}

/**
 * One person, with no figure beside them.
 *
 * The payload carries what the LD-203s filed in their name totalled, and
 * this deliberately does not render it. A report in a lobbyist's name does
 * not have to be their money: the largest of William Kirk's at K&L Gates is
 * $1.8m and every line of it names a contributor of "CONGRESSIONAL BLACK
 * CAUCUS PAC" — a committee he filed for rather than a cheque he wrote.
 * "$2.6m of their own money" against his name would be false, and there is
 * no wording of that figure a reader could act on until the register's own
 * contributor field is parsed. So the panel names people and the money
 * stays in the sections built to qualify it.
 */
function Person({
  person,
  registrantId,
}: {
  person: ProfilePerson;
  registrantId: number | null;
}) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-3">
        {registrantId === null || Number.isNaN(registrantId) ? (
          <span className="font-medium">{person.name}</span>
        ) : (
          <Link
            to={lobbyistPath(registrantId, person.name)}
            className="font-medium text-brand hover:underline"
          >
            {person.name}
          </Link>
        )}
      </div>
      {person.position && (
        // As sworn, uncut. These strings are whole careers and often list
        // three offices; paraphrasing one would make it ours rather than
        // theirs, which is the only thing that makes it worth showing.
        <p className="mt-0.5 text-sm text-muted italic">{person.position}</p>
      )}
      {person.members.length > 0 && (
        // Labelled, and never a bare name on its own line. Unlabelled these
        // read as more of the firm's bench: on one page Steven Horsford and
        // Anthony Brown appeared under the heading "The people" looking like
        // staff at a lobbying firm, when they are the sitting members this
        // lobbyist used to work for. A name under that heading with nothing
        // saying why it is there accuses somebody of a job they do not hold.
        <p className="mt-1 text-sm text-muted">
          <span className="text-faint">Worked for </span>
          {person.members.map((member, at) => (
            <span key={member.bioguide}>
              {at > 0 && <span className="text-faint">, </span>}
              <Link
                to={`/member/${member.bioguide}`}
                className="font-medium text-brand hover:underline"
              >
                {member.full_name ?? member.bioguide}
              </Link>
            </span>
          ))}
        </p>
      )}
    </>
  );
}
